<script setup>
import { ref, watch, computed, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import LocalStorage from '@/controller/LocalStorage.js';
import YautyCell from '@/components/common/yautyTable/YautyCell.vue';
import FontAwesomeRender from '@/components/common/utils/FontAwesomeRender.vue';
import YLoader from "../loaders/YLoader.vue";
import { useRouter } from "vue-router";

const router = useRouter();
const isVehicles = computed(() => router.currentRoute.value.path === '/vehicles')

const props = defineProps({
    isLoading: Boolean,
    noData: Boolean,
    data: Object,
    headers: Array,
    columnsTotal: Number,
    index: Number,
    expandable: Boolean,
    isExpanded: Boolean,
    isExpansion: Boolean,
    tableStates: Object,
    draggable: Boolean,
    table: String
});

const userColor = ref(LocalStorage.getColor());
const userSecondaryColor = ref(LocalStorage.getColorSecondary());
const userTertiaryColor = ref(LocalStorage.getColorTertiary());
const selectedColor = ref(LocalStorage.getColorSelected());
const hoverSelectedColor = ref(LocalStorage.getColorSelectedHover());


const emit = defineEmits(["updateVehicleList","toggleExpand","updateLinkedClient","syncVehicle"]);
const { t } = useI18n();

const toggleExpand = () => {
    emit('toggleExpand', props.isExpanded, props.data, props.index);
}
function updateLinkedClient(client){
    emit('updateLinkedClient', props.data);
}
function syncVehicle(clientId, action){
    emit('syncVehicle', clientId, action);
}

const isMobile = ref(window.innerWidth <= 576);
window.addEventListener('resize', () => {
  isMobile.value = window.innerWidth <= 576;
});

onMounted(() => {
  nextTick(() => {
    const rows = document.querySelectorAll('tr:not(.tr-loading):not(.tr-no-data):not(.td-expansion)');
    if (rows.length === 0) {
      const observer = new MutationObserver(() => {
        const updatedRows = document.querySelectorAll('tr:not(.tr-loading):not(.tr-no-data):not(.td-expansion)');
        if (updatedRows.length > 0) {
          applyAnimation(updatedRows);
          observer.disconnect(); 
          updatedRows.value = 0;
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    } else {
      applyAnimation(rows);
    }
  });
});

function applyAnimation(rows) {
  rows.forEach((row) => {
    row.style.opacity = '0';
    // row.style.transform = 'translateX(40px)';
    row.style.transition = 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1)';
  });
  setTimeout(() => {
    rows.forEach((row) => {
      row.style.opacity = '1';
    //   row.style.transform = 'translateX(0)';
    });
  }, 0);
}
</script>

<template>
    <tr role="row" class="tr-loading" v-if="isLoading">
        <td 
            class="text-center td-loading"
            :colspan="columnsTotal"
            role="cell">
                <div class="loader-wrapper">
                    <YLoader color="primary" />
                </div>
        </td>
    </tr>
    <tr role="row" v-else-if="noData && !isLoading" class="tr-no-data">
        <td 
        class="text-center td-no-data"
        :colspan="columnsTotal"
        role="cell">
            <div class="no-data text-center">
                {{ $t('field.no_data')}}
            </div>
        </td>
    </tr>
    <tr role="row" v-else-if="isExpansion" :class="{'expansion': isExpansion}">
        <td 
            class="td-expansion"
            :class="{'expansion': isExpansion}"
            style="padding: 2.2rem 2rem 3rem 4rem;"
            :colspan="columnsTotal"
            role="cell"
            v-html="data.html">
        </td>
    </tr>
    <tr role="row" v-else :id="props.index" :class="{
                'expanded': isExpanded, 
                'collapsed': !isExpanded && props.table !== 'homePendingWorkfiles' 
                                         && props.table !== 'invitationsHome' 
                                         && props.table !== 'syncClientCarVehicles' 
                                         && props.table !== 'yfsUsersList' 
                                         && props.table !== 'invitationVehiclesList'
                                         && props.table !='configurationUserList' 
                                         && props.table !='organizationClientsList'
                                         && props.table != 'vehicleWorkfilesList' 
                                         && props.table != 'notifications-tablet'
                                         && props.table != 'invitationsHome'
                                         && props.table != 'invitationVehiclesList',
                'vehicleWorkfilesList' : !isExpanded && props.table ==='vehicleWorkfilesList',
                'syncClientCarVehicles' : !isExpanded && props.table === 'syncClientCarVehicles',                           
                'collapsedYfs': !isExpanded && props.table === 'yfsUsersList' 
                                            || props.table === 'configurationUserList', 
                'configurationUserList' : props.table === 'configurationUserList',
                'organizationClientsList' : !isExpanded && props.table === 'organizationClientsList',
                'notifications-tablet' : !isExpanded && props.table === 'notifications-tablet',
                'invitationsHome' : !isExpanded && props.table === 'invitationsHome',
                'invitationVehiclesList' : !isExpanded && props.table === 'invitationVehiclesList',
                }">
        <td role="cell" v-for="v in headers" 
        :class="{
            'pr0': v.field == 'collapse', 
            'pr2': v.field == 'buttons', 
            'action_buttons': v.field == 'buttons' || 
            v.field == 'collapse', 
            'matricula': v.field === 'plate',
            'brand': v.field === 'brand' || v.field === 'image',
            'iveco' : v.field === 'brand' && v.text === 'iveco',
            'model-vehicle' : isVehicles && v.field === 'model',
            'model' : !isVehicles && v.field === 'model',
            'modelFix' : v.field === 'model' && v.navigation === '/workfile/',
            'itv': v.field === 'itvDate',
            'tacografo' : v.field === 'tachographDate',
            'nextVisit': v.field === 'nextVisitDate',
            'deliveryDate' : v.field === 'deliveryDate',
            'vin': v.field === 'vin',
            'client': v.field === 'client',
            'lastChange' : v.field === 'daterange',
            'orderNum' : v.field === 'orNumber',
            'status' : v.field === 'status',
            'status-mobile': v.field == 'status-mobile',
            'taller' : v.field === 'branch',
            'vinPlate' : v.field === 'plate_vin',
            'fix' : v.field === 'reparationTime',
            'cif' : v.field === 'vatNumber',
            'firstName' : v.field === 'firstname',
            'name' : v.field === 'name',
            'lastName' : v.field === 'lastname',
            'secondLastName' : v.field === 'secondLastname',
            'email' : v.field === 'email',
            'carSync' : v.field === 'carSync',
            'linkid' : v.field === 'linkId',
            'address' : v.field === 'address',
            'hasClient' : v.field === 'has_client',
            'read' : v.field === 'read',
            'message' : v.field === 'message',
            'link' :v.field === 'link',
            'table-home-cell' : props.table === 'homePendingWorkfiles',
            'vat_number' : v.field === 'vat_number'
            }"
            v-show="v.visible" :data-label="v.text" class="table-cell">
            <YautyCell 
                :item="data" 
                :field="v.field" 
                :type="v.type"
                :src="v.src"
                :path="v.path"
                :expandable="props.expandable"
                :isExpanded="props.isExpanded"
                :styles="v.styles"
                :tableStates="props.tableStates"
                :navigation="v.navigation"
                :table="props.table"
                :title="v.text"
                @toggleExpand="toggleExpand"
                @updateLinkedClient="updateLinkedClient"
                @syncVehicle="syncVehicle"
            />
        </td>
    </tr>  
</template>  

<style lang="scss" scoped>
.status-mobile{
    display: none;
}
tr td:first-child {
    padding-left: 2rem;
}
.loader-wrapper{
    display: flex;
    justify-content: center;
    align-items: center;
    height: unset !important;
    width: calc(100% + 2.5rem) !important;
    left: -2.5rem;
    top: -5px;
    z-index: 0;
    right: 3rem;
    overflow: visible;
    content: "";
    display: block;
}
.td-loading {
    height: 100%;
    background-color: white !important;
    .loading-cell {
        height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
.td-no-data{
    height: calc(100dvh - 310px);
}
.no-data{
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color:#C8D1DE;
    
    @media (min-width: 1024px) and (orientation: landscape){
        height: 18dvh;
        
    }
    
}
.td-expansion {
    background: #f5f7f9 0% 0% no-repeat padding-box;
    // height: 0 !important;
    // overflow: hidden;
    // interpolate-size: allow-keywords;
    // transition: height 0.3s ease;
    
}
// .expansion {
//     height: auto !important;
// }
th,
tr {
padding: 4px 15px;
}
tr td {
padding: 4px 15px;
border-bottom: 2px solid white;
border: solid 1px #d8d8d8;
border-left: 0;
border-right: 0;
height: 60px;
}
tbody td {
background-clip: padding-box;
}
.pr0{
    padding-right:0;
}
.pr2{
    padding-right:2rem;
}
.table-cell{
        &.modelFix{
            display: none;
        }
    }

@media (max-width: 576px) {
    .status-mobile{
        display: block !important;
    }
    .custom-table-wrapper .table-wrapper .custom-table tbody tr td:first-child, .custom-table-wrapper-home .table-wrapper .custom-table tbody tr td:first-child{
        //width: 100dvw;
        padding-left: inherit !important;
    }

    tr td:first-child {
        padding-left: 0rem !important;
    }
    .notifications-tablet{
        overflow: hidden;
        display: flex;
        flex-direction: column;
        padding-bottom: 35px !important;
        padding-top: 35px !important;
        height: auto !important;
        .table-cell{
            &.read{
                display: none;
            }
            &.message{
                min-height: 170px !important;
                overflow: hidden !important;
            }
            &.link{
                position: absolute !important;
                top:25px;
                right: 20px;
                z-index: 5;
            }
        }
    }
    .collapsed{
        overflow: hidden;
        display: flex;
        flex-direction: column;
        padding-top: 26px !important;
        padding-bottom: 0px !important;
        max-height: 400px !important;
        height: auto !important;
    .table-cell{
        margin-bottom: -25px;
        &.brand{
            order: 1;
            position: absolute !important;
            left: 1rem;
            top: 20px;
        }
        &.modelFix{
            order: 2;
            top: 38px !important;
            font-size: 20px;
        }
        &.vinPlate{
            order: 3 !important;
            margin-top: 42px;
        }
        &.orderNum{
            order: 4 !important;
            margin-top: 3px;
        }
        &.status{
            order: 5 !important;
            margin-top: 8px;
            margin-bottom: 10px !important;
        }
        &.taller{
            order: 4 !important;
        }
        &.fix{
            order: 8 !important;
            display: none;
        }
        &.lastChange{
            order: 9 !important;
            display: none;
        }
        &.model{
            order: 2;
            font-size: 20px;
            margin-top: -25px
        }
        &.model-vehicle{
            order: 2;
            font-size: 20px;
            margin-top: 40px;
            margin-left: -4px;
        }
        &.matricula{
            order: 3;
            font-size: 20px;
            margin-top: 20px;
            margin-left: -77px;
        }
        &.vin{
            order: 4;
        }

        &.client{
            order: 5;
        }

        &.itv{
            display: none;
            order: 6;
            margin-top: -28px;
        }

        &.nextVisit{
            order: 7;
        }

        &.tacografo {
            order: 8;
            margin-bottom: 0px;
        }
    }
}
.collapsedYfs, .syncClientCarVehicles, .configurationUserList{
    overflow: show;
    display: flex;
    flex-direction: column;
    padding-bottom: 35px !important;
    padding-top: 25px !important;
    .table-cell{
        
        &.cif{
            order: 1 !important;
            margin-left: 0px;
        }
        &.name{
            order: 2 !important;
            margin-left: 0px
        }
        &.firstName{
            order: 2 !important;
        }

        &.lastName{
            order: 3 !important;
        }

        &.secondLastName{
            order: 4 !important;
        }

        &.email{
            order: 5 !important;
        }

        &.carSync{
            order: 6 !important;
            height: auto !important;
            margin-top: 0.5rem;
        }
        
        &.address{
            order: 7 !important;
            display: none; // per ara
        }

        &.linkid{
            order: 8 !important;
            z-index: 5;
            width: 70px;
            position: absolute !important;
            right: 25px;
        }
        &.action_buttons{
            order: 8;
            display: block;
            right: 15px;
            z-index: 5;
        }
    }
}
.tr-loading, .text-center.td-loading{
    background-color: transparent !important;
}
.organizationClientsList{
    width: auto !important;
    padding-bottom: 35px !important;
    padding-top: 25px !important;
    .table-cell{
       &.hasClient{
        margin-left: -45px !important;
       }
    }
}
}

@media (min-width: 577px) and (max-width: 1405px) and (orientation: portrait){
    .invitationsHome{
        .table-cell{
            &.pr2{
                position: absolute !important;
                top: 0 !important;
                right: 25px !important;
                }
        }
    }
    .notifications-tablet{
        overflow: hidden;
        display: flex;
        flex-direction: column;
        height: auto !important;
        padding-bottom: 110px !important;
        .table-cell{
            &.read{
                display: none;
            }
            &.link{
                position: absolute !important;
                bottom: 0;
                left: 15px;
                z-index: 5;
            }
        }
    }
    .status-mobile{
        display: block !important;
    }
    .tr-loading, .text-center.td-loading{
        background-color: transparent !important;
    }
    .custom-table-wrapper .table-wrapper .custom-table tbody tr td:first-child, .custom-table-wrapper-home .table-wrapper .custom-table tbody tr td:first-child{
        //width: 100dvw;
        margin-bottom: 0em;
        padding-left: inherit !important;
    }
    .vehicleWorkfilesList{
        display: grid;
        grid-template-columns: 1fr 1fr 40px 1fr;
        .table-cell{
            min-width: 100px !important;
        }
    }
    .collapsed{
        overflow: hidden;
        display: flex;
        flex-direction: column;
        padding-top: 30px !important;
        padding-bottom: 40px !important;
        margin-bottom: 30px;
        .table-cell{
            margin-bottom: -25px;
            &.brand{
                order: 1;
                position: absolute !important;
                left: 0.8rem;
                top: 15px;
            }
            &.orderNum{
                order: 2 !important;
                margin-bottom: -22px;
            }
            &.taller{
                order: 3 !important;
                margin-top: 5px;   
                margin-bottom: -22px;
            }
            &.modelFix{
                display: none !important;
            }
            &.vinPlate{
                order: 4 !important;
                margin-top: 5px;
                margin-bottom: -22px;
            }
            
            &.status{
                order: 7 !important;
                margin-top: -40px;
                margin-bottom: 5px;
            }
            
            &.fix{
                order: 6 !important;
                display: none;
            }
            &.lastChange{
                order: 5 !important;
                margin-top: 10px;
            }
            &.deliveryDate{
                display: none !important;
            }
            &.model-vehicle{
            order: 2;
            font-size: 20px;
            margin-top: 40px;
            margin-left: -4px;
            }
            &.model{
                order: 2;
                left:3.2rem;
                font-size: 20px;
            }
            &.matricula{
            order: 3;
            font-size: 20px;
            margin-left: -15px;
            }
            &.vin{
                order: 4;
            }

            &.client{
                order: 5;
            }

            &.itv{
                order: 6;
                display: none;
            }

            &.nextVisit{
                order: 7;
            }

            &.tacografo {
                order: 8;
            }
            &.pr2{
                display: none;
            }
            &.pr0{
                display: none;
            }
        }
    }
    .collapsedYfs, .syncClientCarVehicles{
        width: auto !important;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        padding-bottom: 35px !important;
        padding-top: 25px !important;
        .table-cell{
            margin-bottom: -10px;
        &.cif{
            order: 1 !important;
            //margin-left: 14px;
        }
        &.name{
            order: 2 !important;
            //margin-left: -23px
        }
        &.firstName{
            order: 2 !important;
        }

        &.lastName{
            order: 3 !important;
        }

        &.secondLastName{
            order: 4 !important;
        }

        &.email{
            order: 5 !important;
        }

        &.carSync{
            order: 6 !important;
        }
        
        &.address{
            order: 7 !important;
            display: none; // per ara
        }

        &.linkid{
            z-index: 5;
            width: 70px;
            position: absolute !important;
            right: 25px;
        }
        &.action_buttons{
            display: block;
            z-index: 5;
            width: 70px;
            position: absolute !important;
            right: 25px;
        }
    }
}

.syncClientCarVehicles{
    @media  (min-width: 687px) and (max-width: 704px) and (orientation: portrait){
        padding-bottom: 50px !important;
    }
}

.organizationClientsList{
    width: auto !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-bottom: 35px !important;
    padding-top: 25px !important;
    .table-cell{
       &.hasClient{
        margin-left: -45px !important;
       }
    }
}

.tr-loading, .text-center.td-loading{
        background-color: transparent !important;
    }
}

@media (min-width:577px) and (max-width: 686px){
    .syncClientCarVehicles{
        display: flex;
        flex-direction: column;
        padding-bottom: 40px !important;
        .table-cell{
            &.carSync{
                // position: absolute !important;
                bottom: 10px;
                left: 10px;
            }
        }
    }
}

@media (min-width: 577px) and (max-width: 1024px) and (orientation: portrait){
    .invitationVehiclesList{
        display: flex;
        flex-direction: column;
        padding: 10px !important;
        overflow: auto;
        
        height: auto;
        
        bottom: 0rem;
        .table-cell{
            
        
            &.model{
                // position: absolute !important;
                
                
            }
            &.matricula{
                // position: absolute !important;
                // bottom: 10px;
                // left: 10px;
            }

            &.itv{
                left: 1rem;
                
            }

            
        }
        
    }
}
.fade-in-left {
  opacity: 0;
  transform: translateX(40px);
  transition: opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-in-left-active {
  opacity: 1;
  transform: translateX(0);
}
</style>

